import { useAccount } from "@qw/auth";
import {
  useInboxDraftSentSubscription,
  useInboxDraftsCountQuery,
  useInboxDraftsFailedScheduledSendsCountQuery,
  useInboxLabelCountsUpdatedSubscription,
  useInboxLabelUnreadCountsQuery,
  useInboxMessageCreatedSubscription,
  useInboxMessageReadySubscription,
} from "@qw/graphql";
import { KnownInboxLabel } from "@qw/qw-common";
import { useCallback, useEffect, useMemo, useRef } from "react";

const SYSTEM_COUNT_LABELS = [KnownInboxLabel.INBOX, KnownInboxLabel.STARRED];

export const useFolderCounts = (userLabels: string[] = [], insuredId?: string) => {
  const { userInbox } = useAccount();
  const labels = useMemo(() => [...new Set([...SYSTEM_COUNT_LABELS, ...userLabels])], [userLabels]);

  const { data: labelCountsData, refetch: refetchLabelCounts } = useInboxLabelUnreadCountsQuery({
    variables: { input: { insuredId, labels } },
    fetchPolicy: "cache-and-network",
  });

  const { data: draftsData, refetch: refetchDraftsCount } = useInboxDraftsCountQuery({
    variables: { input: { insuredId } },
    fetchPolicy: "cache-and-network",
  });

  const { data: failedScheduledSendsData, refetch: refetchFailedScheduledSendsCount } =
    useInboxDraftsFailedScheduledSendsCountQuery({
      pollInterval: 5000,
    });

  const refetchInboxCounts = useCallback(() => {
    void refetchLabelCounts();
  }, [refetchLabelCounts]);
  const labelCountsUpdateRefetchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const labelCountsUpdateRefetchPendingRef = useRef(false);

  const scheduleLabelCountsUpdateRefetch = useCallback(() => {
    labelCountsUpdateRefetchPendingRef.current = true;

    if (labelCountsUpdateRefetchTimeoutRef.current) {
      return;
    }

    labelCountsUpdateRefetchTimeoutRef.current = setTimeout(() => {
      labelCountsUpdateRefetchTimeoutRef.current = null;
      if (!labelCountsUpdateRefetchPendingRef.current) {
        return;
      }

      labelCountsUpdateRefetchPendingRef.current = false;
      void refetchLabelCounts();
    }, 250);
  }, [refetchLabelCounts]);

  const refetchDraftCounts = useCallback(() => {
    void refetchDraftsCount();
    void refetchFailedScheduledSendsCount();
  }, [refetchDraftsCount, refetchFailedScheduledSendsCount]);

  useInboxMessageCreatedSubscription({
    variables: { email: userInbox?.email ?? "" },
    onData: refetchInboxCounts,
    skip: !userInbox?.email,
  });

  useInboxMessageReadySubscription({
    variables: { email: userInbox?.email ?? "" },
    onData: refetchInboxCounts,
    skip: !userInbox?.email,
  });

  useInboxLabelCountsUpdatedSubscription({
    variables: { email: userInbox?.email ?? "" },
    onData: scheduleLabelCountsUpdateRefetch,
    skip: !userInbox?.email,
  });

  useInboxDraftSentSubscription({
    variables: { email: userInbox?.email ?? "" },
    onData: refetchDraftCounts,
    skip: !userInbox?.email,
  });

  useEffect(() => {
    return () => {
      if (labelCountsUpdateRefetchTimeoutRef.current) {
        clearTimeout(labelCountsUpdateRefetchTimeoutRef.current);
      }
      labelCountsUpdateRefetchPendingRef.current = false;
    };
  }, []);

  const labelCounts = useMemo(
    () => new Map(labelCountsData?.inboxLabelUnreadCounts.map(({ count, label }) => [label, count]) ?? []),
    [labelCountsData?.inboxLabelUnreadCounts]
  );

  const getCount = useCallback((label: string) => labelCounts.get(label), [labelCounts]);

  return {
    draftsCount: draftsData?.inboxDraftsCount,
    getCount,
    hasFailedScheduledSends: !!failedScheduledSendsData?.inboxDraftsFailedScheduledSendsCount,
  };
};
